import { z } from "zod";

export const reviewReferenceSchema = z
  .object({
    contract_id: z.string().min(1),
    record_id: z.string().min(1),
    record_version: z.number().int().positive(),
    principal_id: z.string().min(1),
  })
  .strict();
export type ReviewReference = z.infer<typeof reviewReferenceSchema>;
export const recoveryOperationSchema = z.enum([
  "resume",
  "cancel",
  "refresh",
  "reconcile",
  "rollback",
]);
export const reviewCommandSchema = z.discriminatedUnion("command", [
  z
    .object({
      command: z.literal("effort"),
      target: reviewReferenceSchema,
      session_id: z.string().min(1).max(128),
      started_at: z.number().int(),
      ended_at: z.number().int(),
      activity_times: z.array(z.number().int()).max(256),
      foreground: z.boolean(),
    })
    .strict(),
  z
    .object({
      command: z.literal("feedback"),
      target: reviewReferenceSchema,
      useful: z.boolean(),
    })
    .strict(),
  z
    .object({ command: z.literal("forget"), target: reviewReferenceSchema })
    .strict(),
  z
    .object({
      command: z.literal("recover"),
      target: reviewReferenceSchema,
      operation: recoveryOperationSchema,
    })
    .strict(),
  z
    .object({
      command: z.literal("correct"),
      target: reviewReferenceSchema,
      evidence: z.array(reviewReferenceSchema).min(1),
      patch: z.record(z.string(), z.unknown()),
    })
    .strict(),
  z
    .object({
      command: z.literal("update_preference"),
      target: reviewReferenceSchema,
      evidence: z.array(reviewReferenceSchema).min(1),
      patch: z.record(z.string(), z.unknown()),
    })
    .strict(),
  z
    .object({
      command: z.literal("cancel"),
      target: reviewReferenceSchema,
      reason: z.string().min(1).max(2000),
    })
    .strict(),
]);
export const reviewIntentSchema = z.discriminatedUnion("operation", [
  z.object({ operation: z.literal("list") }).strict(),
  z
    .object({
      operation: z.literal("list_page"),
      after: reviewReferenceSchema.nullable(),
      limit: z.number().int().min(1).max(50),
    })
    .strict(),
  z
    .object({ operation: z.literal("open"), target: reviewReferenceSchema })
    .strict(),
  z
    .object({ operation: z.literal("command"), command: reviewCommandSchema })
    .strict(),
]);
export type ReviewIntent = z.infer<typeof reviewIntentSchema>;
export const reviewViewSchema = z
  .object({
    subject: reviewReferenceSchema,
    content: z
      .object({
        media_type: z.string(),
        text: z.string().max(65536),
        truncated: z.boolean(),
      })
      .strict()
      .nullable()
      .optional(),
    temporal_state: z.enum(["current", "historical", "proposed"]),
    artifact: reviewReferenceSchema.nullable(),
    artifact_hash: z.string().nullable(),
    input_versions: z.array(reviewReferenceSchema),
    receipts: z.array(reviewReferenceSchema),
    assumptions: z.array(z.string()),
    costs: z.unknown(),
    capability_gaps: z.array(z.string()),
    unavailable: z.array(z.string()),
    recovery_action: z.string().nullable(),
    can_forget: z.boolean().optional(),
    feedback_requested: z.boolean().optional(),
    recovery: z
      .object({ operation: recoveryOperationSchema })
      .strict()
      .nullable()
      .optional(),
  })
  .strict();
export type ReviewView = z.infer<typeof reviewViewSchema>;

export const reviewPageSchema = z
  .object({
    items: z.array(reviewViewSchema).max(50),
    next_cursor: reviewReferenceSchema.nullable(),
  })
  .strict();
